/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Dialog, Button, TextField, Table, TableBody, TableRow, TableCell, Paper, Typography, TableHead,
  Box, Chip, TableContainer, Alert, Snackbar, CircularProgress, LinearProgress, TableFooter, Pagination, InputAdornment, alpha, useTheme, useMediaQuery,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ProdutosService, IListagemDeProdutos } from '../../../shared/services/api/estoque/produtos/ProdutosService';
import { v4 as uuidv4 } from 'uuid';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import KeyboardRoundedIcon from '@mui/icons-material/KeyboardRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Environment } from '../../../shared/environment';
import { OrdenarPorControl, ListagemBuscaField, IOrdenarPorOpcao } from '../../../shared/components';

interface IitensEntradaDialog {
  iditens: string;
  idprodutos: string;
  idref: string;
  qtds: number;
  qtde: number;
  un: string;
  preco: number;
  ean?: string;
  subtotal: number;
  desconto: number;
  voutros: number;
  total: number;
  filtro: string;
  referencia?: string;
  marca?: string;
  descricao: string;
  anp?: string;
  original?: boolean;
  localizado?: string;
  status?: number;
}
interface Props {
  open: boolean;
  onClose: () => void;
  // Chamado a cada item adicionado com sucesso — o item já deve ser salvo na listagem da
  // venda (rows do pai) imediatamente, não só ao fechar a dialog (a pedido: "quando adicionar
  // o item ele já deve ser salvo na listagem da venda"). Se o produto já estava na venda, vem
  // com o MESMO `iditens` (quantidade somada) — quem chama deve fazer upsert por `iditens`,
  // não um push incondicional.
  onAdicionarItem: (item: IitensEntradaDialog) => void;
  // Itens JÁ salvos na venda (rows do pai, sem os deletados) — fonte de verdade tanto pros
  // contadores do cabeçalho (Itens na venda/Total da venda) quanto pra detectar que um produto
  // selecionado já está na venda (soma quantidade em vez de duplicar linha).
  itensAtuais: IitensEntradaDialog[];
  decimais: number;
  est: boolean;
  // Orçamento não reserva/baixa estoque de verdade — permite adicionar itens mesmo sem
  // estoque disponível (a checagem de verdade acontece depois, se a operação virar Venda à
  // Vista/Prazo, ver DetalheDeVenda.tsx). Default false — comportamento inalterado.
  permitirSemEstoque?: boolean;
}

const tiposBuscaProduto: IOrdenarPorOpcao[] = [
  { nameBD: 'descricao', descricao: 'Descrição' },
  { nameBD: 'referencia', descricao: 'Referência' },
  { nameBD: 'codinterno', descricao: 'Código Interno' },
];

const modosDeBuscaProduto: IOrdenarPorOpcao[] = [
  { nameBD: 'inicio', descricao: 'Início do texto' },
  { nameBD: 'contem', descricao: 'Em qualquer parte do texto' },
];

interface ICachePaginaProdutos {
  pagina: number;
  contexto: string;
  data: IListagemDeProdutos[];
  totalCount: number;
}

// Paleta própria desta dialog (design fornecido) — cores de estado (sucesso/atenção)
// deliberadamente fixas, não vêm do tema geral do app. O destaque "primário" (ícones, seleção
// de linha, painel do produto selecionado) usa theme.palette.primary.main em vez de um azul
// fixo, pra acompanhar o ciano de marca do sistema — um azul independente destoava do resto do
// app, especialmente no tema escuro.
const PALETA = {
  sucesso: '#16A34A',
  atencao: '#FFB90A',
};

// Card de estatística do cabeçalho (Itens na venda / Total da venda / Teclas de atalho) —
// mesmo formato nos três, só ícone/cor/conteúdo mudam.
const CardEstatistica: React.FC<{ icon: React.ReactNode; label: string; valor: React.ReactNode; corValor?: string }> = ({ icon, label, valor, corValor }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.75, sm: 1 },
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 0.75, sm: 1 },
        borderRadius: '12px',
        border: `1px solid ${theme.torq.border.default}`,
        backgroundColor: theme.torq.surface.card,
        minWidth: { xs: 108, sm: 140 },
      }}
    >
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: { xs: '0.62rem', sm: '0.68rem' }, color: 'text.secondary', lineHeight: 1.3 }}>
          {label}
        </Typography>
        <Typography noWrap sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700, color: corValor ?? theme.torq.text.primary, lineHeight: 1.3 }}>
          {valor}
        </Typography>
      </Box>
    </Box>
  );
};

export const DialogVenda: React.FC<Props> = ({
  open,
  onClose,
  onAdicionarItem,
  itensAtuais,
  decimais,
  est,
  permitirSemEstoque = false,
}) => {
  const theme = useTheme();
  // Tela pequena (largura curta OU altura curta — janela redimensionada, notebook de tela
  // menor, monitor de baixa resolução) — em vez de encolher tudo com zoom/transform:scale
  // (que trata a interface como uma imagem e não se adapta de verdade ao espaço real), a
  // dialog vira fullScreen e os elementos internos usam tamanhos reais menores (ver sx
  // responsivos abaixo) — cada coisa ocupa exatamente o espaço que faz sentido pra tela atual.
  const larguraPequena = useMediaQuery(theme.breakpoints.down('md'));
  const alturaPequena = useMediaQuery('(max-height:700px)');
  const telaPequena = larguraPequena || alturaPequena;
  // Celular (< 600px): layout de app — produtos em cartões, sem a tabela
  // (que tem colunas demais pra tela). Como não há tabela pra medir, o número
  // de produtos por página é fixo (a lista rola), ver calcularLimiteDinamico.
  const celular = useMediaQuery(theme.breakpoints.down('sm'));
  const PRODUTOS_POR_PAGINA_CELULAR = 20;
  const requestIdRef = useRef(0);
  // Guarda em qual ponta a próxima página deve focar quando a troca de página vem da
  // navegação por teclado (seta no primeiro/último item — ver handleKeyDown); null quando a
  // página muda por outro motivo (filtro, tipo de busca, clique na paginação).
  const focoNaTrocaDePaginaRef = useRef<'primeiro' | 'ultimo' | null>(null);
  const [produtos, setProdutos] = useState<IListagemDeProdutos[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filtro, setFiltro] = useState('');
  const [tipoBusca, setTipoBusca] = useState('descricao');
  // 'inicio' por padrão — busca só pelo começo do texto, mais rápida e mais
  // previsível pro operador digitando durante a venda.
  const [modoBusca, setModoBusca] = useState('inicio');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  // Página adjacente (anterior/seguinte) pré-carregada em segundo plano —
  // permite a seta pra cima/baixo "rolar" através do limite da página sem
  // o flash de carregando de uma busca nova (ver efeitos de fetch/prefetch).
  const cacheAdjacenteRef = useRef<{ anterior: ICachePaginaProdutos | null; seguinte: ICachePaginaProdutos | null }>({
    anterior: null,
    seguinte: null,
  });
  const contextoBusca = useMemo(
    () => `${tipoBusca}|${filtro}|${modoBusca}|${rowsPerPage}`,
    [tipoBusca, filtro, modoBusca, rowsPerPage],
  );
  const [isLoading, setIsLoading] = useState(false);
  // Totais do cabeçalho vêm da venda de verdade (itensAtuais, do pai) — não de uma lista local
  // desta dialog — já que cada item adicionado é salvo imediatamente na venda (ver
  // onAdicionarItem), não existe mais uma "sessão" de itens só desta dialog pra somar.
  const qtdItens = useMemo(() => itensAtuais.reduce((soma, item) => soma + item.qtds, 0), [itensAtuais]);
  const totalValor = useMemo(() => itensAtuais.reduce((soma, item) => soma + item.subtotal, 0), [itensAtuais]);
  const [produtoSelecionado, setProdutoSelecionado] = useState<IListagemDeProdutos | null>(null);
  // Dígitos brutos digitados no campo Quantidade (sem separadores). Enquanto a quantidade de
  // dígitos for menor ou igual às casas decimais, o valor é tratado como inteiro puro (ex.: "12"
  // com 2 casas decimais continua "12"); só ao ultrapassar isso é que passa a deslocar casas
  // decimais como no Preço Unitário (ex.: "123" vira "1,23").
  const [qtdDigits, setQtdDigits] = useState('1');
  const qtdInputRef = useRef<HTMLInputElement>(null);
  const [precoUnitario, setPrecoUnitario] = useState<number>(0);
  const buscaRef = useRef<HTMLInputElement>(null);
  const tabelaRef = useRef<HTMLDivElement>(null);
  const footerRowRef = useRef<HTMLTableRowElement>(null);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const precoInputRef = useRef<HTMLInputElement>(null);
  const btnAddRef = useRef<HTMLButtonElement>(null);
  const [limparFiltro, setLimparFiltro] = useState(false);
  // Produto sem estoque que o usuário tentou selecionar — dispara o dialog perguntando se
  // ele quer ir ajustar o estoque desse produto (abre o cadastro dele em outra aba). null
  // quando o dialog está fechado.
  const [produtoSemEstoque, setProdutoSemEstoque] = useState<IListagemDeProdutos | null>(null);
  const ultimaAlturaMedidaRef = useRef<number>(0);
  // Guardam a altura REAL medida no DOM (linha de produto e rodapé de paginação), assim que
  // ficam disponíveis, em vez de depender só dos valores estimados abaixo — evita que um
  // ajuste futuro de fonte/ícone/chip desalinhe a conta e volte a estourar o scroll.
  const alturaLinhaMedidaRef = useRef<number | null>(null);
  const alturaFooterMedidaRef = useRef<number | null>(null);

  // Estimativas usadas somente até a primeira medição real estar disponível (linha e rodapé
  // ainda não renderizados na primeira passada). Depois disso, alturaLinhaMedidaRef e
  // alturaFooterMedidaRef (medidos direto no DOM) têm prioridade — não dependemos apenas de um
  // valor fixo que desalinha sempre que um Chip/ícone/fonte muda de tamanho.
  const ALTURA_LINHA_PADRAO = 30;
  const FOOTER_RESERVADO_PADRAO = 56;

  // Recebe a altura já medida (do próprio ResizeObserver ou de uma leitura síncrona no
  // mount) em vez de reler o layout aqui dentro. Ler getBoundingClientRect de dois elementos
  // irmãos (container e painel) numa segunda passada, agendada via requestAnimationFrame após
  // dois listeners redundantes (ResizeObserver + window "resize"), criava uma corrida onde
  // resizes em sequência perdiam a atualização e o limite ficava preso no último valor que
  // conseguiu ser lido — por isso agora medimos a altura do próprio container uma única vez,
  // no mesmo callback que detectou a mudança.
  const calcularLimiteDinamico = (alturaContainer: number) => {
    if (celular) {
      setRowsPerPage(prev => (prev === PRODUTOS_POR_PAGINA_CELULAR ? prev : PRODUTOS_POR_PAGINA_CELULAR));
      return;
    }
    const container = tabelaRef.current;
    if (!container) {
      return;
    }

    // O rodapé de paginação só existe no DOM quando totalCount > rowsPerPage, então usamos a
    // última altura real medida (alturaFooterMedidaRef) quando disponível; sem isso ainda,
    // caímos na estimativa fixa.
    const FOOTER_RESERVADO = alturaFooterMedidaRef.current ?? FOOTER_RESERVADO_PADRAO;

    // O <thead> é sempre renderizado, mesmo sem produtos carregados, então dá para medi-lo
    // desde a abertura do modal — diferente da linha do corpo, ele não depende de dados terem
    // chegado da API.
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 34;

    // Idem para a altura de linha: usa a medição real (feita assim que a primeira leva de
    // produtos renderiza) quando disponível, senão a estimativa fixa.
    const alturaLinha = alturaLinhaMedidaRef.current ?? ALTURA_LINHA_PADRAO;

    const alturaAtual = Math.round(alturaContainer);

    // Ignora recálculos disparados por variações de sub-pixel (comuns em monitores com
    // escala fracionária, ex.: 125%/150%), que não mudam a quantidade real de linhas cabíveis
    // mas mudam a altura medida em frações, reacionando o ResizeObserver indefinidamente.
    if (Math.abs(alturaAtual - ultimaAlturaMedidaRef.current) < 2) {
      return;
    }
    ultimaAlturaMedidaRef.current = alturaAtual;

    const MIN_ROWS = 4;
    const MAX_ROWS = 50;
    // Com a altura de linha e de rodapé só estimadas (medição real ainda não disponível),
    // reserva uma linha inteira de folga para não estourar o scroll; uma vez com as duas
    // medições reais, a folga de meia linha já cobre erros de arredondamento.
    const FOLGA_LINHAS = (alturaLinhaMedidaRef.current !== null && alturaFooterMedidaRef.current !== null) ? 0.5 : 1;
    const alturaDisponivel = alturaAtual - alturaHeader - FOOTER_RESERVADO;

    // Se não sobrou altura calculável (ex.: medição durante um layout intermediário), mantém
    // o rowsPerPage atual em vez de forçar o piso mínimo — evita que uma leitura ruim e
    // pontual derrube o limite para 5 quando há espaço de sobra na tela.
    if (alturaDisponivel <= 0) {
      return;
    }

    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / alturaLinha - FOLGA_LINHAS))
    );

    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect (não useEffect) para rodar de forma síncrona antes da pintura e, mais
  // importante, antes do efeito de busca (que depende de rowsPerPage) disparar a requisição
  // inicial — assim a primeira busca ao abrir o modal já sai com o limite correto, sem uma
  // segunda requisição de correção.
  useLayoutEffect(() => {
    if (!open) return;

    ultimaAlturaMedidaRef.current = 0;

    const container = tabelaRef.current;
    if (!container) return;

    calcularLimiteDinamico(container.getBoundingClientRect().height);

    // Único gatilho de recálculo: o ResizeObserver já dispara para qualquer mudança real de
    // tamanho do container (resize da janela, zoom, F11, abrir/fechar DevTools), então um
    // listener redundante de "resize" da window só serve para competir com ele por um mesmo
    // requestAnimationFrame agendado — em resizes em sequência isso podia cancelar/reagendar o
    // frame na hora errada e perder atualizações, deixando o limite preso no último valor lido.
    // Aqui a própria entry do observer já traz a altura atualizada (contentRect), então o
    // cálculo roda direto no callback, sem uma segunda leitura de layout depois.
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        calcularLimiteDinamico(entry.contentRect.height);
      }
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  useEffect(() => {
    if (!open) return;

    // Página adjacente já pré-carregada em segundo plano (mesmo contexto de busca) — troca na
    // hora, sem loading nem ida ao servidor, pra rolagem fluida ao cruzar o limite da página
    // com as setas (ver efeito de prefetch logo abaixo).
    const cacheAtual = cacheAdjacenteRef.current;
    const cache = cacheAtual.anterior?.pagina === page && cacheAtual.anterior?.contexto === contextoBusca
      ? cacheAtual.anterior
      : cacheAtual.seguinte?.pagina === page && cacheAtual.seguinte?.contexto === contextoBusca
        ? cacheAtual.seguinte
        : null;
    if (cache) {
      cacheAdjacenteRef.current = { anterior: null, seguinte: null };
      ++requestIdRef.current;
      setIsLoading(false);
      setProdutoSelecionado(null);
      setProdutos(cache.data);
      setTotalCount(cache.totalCount);
      if (focoNaTrocaDePaginaRef.current === 'ultimo') {
        setSelectedIndex(Math.max(cache.data.length - 1, 0));
      } else if (focoNaTrocaDePaginaRef.current === 'primeiro') {
        setSelectedIndex(0);
      }
      focoNaTrocaDePaginaRef.current = null;
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    // Zera "produtos" imediatamente ao disparar uma nova busca, para nunca deixar itens da
    // pesquisa anterior visíveis junto com/antes do resultado atual. O loop de recálculo de
    // rowsPerPage que antes causava um flash duplo ao abrir o modal já foi corrigido na raiz
    // (scrollbarGutter estável + medidas de linha/cabeçalho congeladas), então limpar aqui não
    // reintroduz aquele problema.
    setProdutos([]);
    setTotalCount(0);
    setProdutoSelecionado(null);

    // Se a página mudou por navegação de teclado (seta no primeiro/último item da página
    // atual — ver handleKeyDown), o índice final só é decidido depois que os produtos da nova
    // página chegarem (abaixo); qualquer outro gatilho (digitar filtro, trocar tipo de busca,
    // trocar de página pelo controle de paginação) já entra direto no primeiro item.
    if (!focoNaTrocaDePaginaRef.current) {
      setSelectedIndex(0);
    }

    ProdutosService.getAll(page + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then(res => {
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);

      if (!(res instanceof Error)) {
        setProdutos(res.data);
        setTotalCount(res.totalCount || 0);

        if (focoNaTrocaDePaginaRef.current === 'ultimo') {
          setSelectedIndex(Math.max(res.data.length - 1, 0));
        } else if (focoNaTrocaDePaginaRef.current === 'primeiro') {
          setSelectedIndex(0);
        }
        focoNaTrocaDePaginaRef.current = null;
      } else {
        focoNaTrocaDePaginaRef.current = null;
        setProdutos([]);
        setTotalCount(0);
        setMsgType('error');
        setMsg(res.message);
        setOpenMsg(true);
      }
    });

  }, [filtro, tipoBusca, modoBusca, open, page, rowsPerPage, contextoBusca]);

  // Pré-carrega a página anterior e a seguinte assim que a atual termina de carregar —
  // silencioso (não mexe em isLoading/produtos), só alimenta o cache acima.
  useEffect(() => {
    if (!open || isLoading || produtos.length === 0) return;
    const totalPaginasAtual = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    const alvoSeguinte = page + 1 < totalPaginasAtual ? page + 1 : null;
    const alvoAnterior = page > 0 ? page - 1 : null;
    const cacheAtual = cacheAdjacenteRef.current;
    let cancelado = false;

    if (alvoSeguinte !== null && !(cacheAtual.seguinte?.pagina === alvoSeguinte && cacheAtual.seguinte?.contexto === contextoBusca)) {
      ProdutosService.getAll(alvoSeguinte + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then((result) => {
        if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
        cacheAdjacenteRef.current = {
          ...cacheAdjacenteRef.current,
          seguinte: { pagina: alvoSeguinte, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
        };
      });
    }
    if (alvoAnterior !== null && !(cacheAtual.anterior?.pagina === alvoAnterior && cacheAtual.anterior?.contexto === contextoBusca)) {
      ProdutosService.getAll(alvoAnterior + 1, filtro, tipoBusca, rowsPerPage, modoBusca).then((result) => {
        if (cancelado || result instanceof Error || !result.data || result.data.length === 0) return;
        cacheAdjacenteRef.current = {
          ...cacheAdjacenteRef.current,
          anterior: { pagina: alvoAnterior, contexto: contextoBusca, data: result.data, totalCount: result.totalCount },
        };
      });
    }

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalCount, isLoading, contextoBusca, open]);

  // Assim que uma linha de produto real (tem data-row-index; a de "carregando" e a de "vazio"
  // não têm) e/ou o rodapé de paginação são renderizados, mede a altura real deles no DOM e
  // substitui as estimativas usadas em calcularLimiteDinamico. Só corrige a estimativa da
  // PRIMEIRA medição em diante (ref ainda null) — nunca mais depois disso, mesmo que a página
  // seguinte meça uma altura diferente (nome de produto quebrando em 2 linhas, chip de "sem
  // estoque" só em alguns itens etc.). Sem essa trava, cada busca/página nova podia disparar um
  // recálculo por causa dessa variação de CONTEÚDO (não de tamanho real do container), mudando
  // rowsPerPage e resetando a página pro início no meio da navegação (ver
  // useEffect(() => setPage(0), [rowsPerPage]) abaixo) — o comentário já dizia "no máximo uma
  // vez por abertura", mas a comparação era contra a ÚLTIMA medição, não contra "já corrigiu".
  useLayoutEffect(() => {
    if (!open) return;
    const container = tabelaRef.current;
    if (!container) return;

    let mudou = false;

    const linhaEl = container.querySelector('tbody tr[data-row-index]');
    if (linhaEl && alturaLinhaMedidaRef.current === null) {
      const alturaLinha = Math.round(linhaEl.getBoundingClientRect().height);
      if (alturaLinha > 0) {
        alturaLinhaMedidaRef.current = alturaLinha;
        mudou = true;
      }
    }

    const footerEl = footerRowRef.current;
    if (footerEl && alturaFooterMedidaRef.current === null) {
      const alturaFooter = Math.round(footerEl.getBoundingClientRect().height);
      if (alturaFooter > 0) {
        alturaFooterMedidaRef.current = alturaFooter;
        mudou = true;
      }
    }

    if (mudou) {
      // Força a recalcular mesmo que a altura do container não tenha mudado: o espaço
      // disponível é o mesmo, mas agora com uma altura de linha/rodapé mais precisa.
      ultimaAlturaMedidaRef.current = -1;
      calcularLimiteDinamico(container.getBoundingClientRect().height);
    }
  }, [produtos, totalCount, rowsPerPage, open]);

  const formatNumber = (
    value: number | null | undefined,
    options?: Intl.NumberFormatOptions
  ) => {
    return Number(value ?? 0).toLocaleString('pt-BR', options);
  };

  const formatMoney = (value: number) => formatNumber(value, { style: 'currency', currency: 'BRL' });

  // Mesma fórmula usada em DetalheDeProduto.tsx (calcularPrecoCusto): preço de compra + comissão
  // e icms como percentual do preço de compra, frete também como percentual, e seguro como valor
  // fixo já em reais.
  const calcularPrecoCusto = (prod: IListagemDeProdutos): number => {
    const preco = prod.preco ?? 0;
    const comissao = prod.comissao ?? 0;
    const icms = prod.icms ?? 0;
    const frete = prod.pfrete ?? 0;
    const seguro = prod.pseguro ?? 0;

    return (
      preco +
      preco * (comissao / 100) +
      preco * (icms / 100) +
      preco * (frete / 100) +
      seguro
    );
  };

  const qtd =
    qtdDigits.length <= decimais
      ? Number(qtdDigits || 0)
      : Number(qtdDigits) / Math.pow(10, decimais);
  // Abaixo do limiar de casas decimais, mostra os dígitos brutos (preservando zeros à
  // esquerda, ex.: "02") em vez de convertê-los em Number — senão o próximo dígito digitado
  // perderia esse zero ao ser lido de volta do campo, impedindo digitar algo como "0,25".
  const qtdDisplay =
    qtdDigits.length <= decimais
      ? qtdDigits
      : formatNumber(qtd, { minimumFractionDigits: decimais, maximumFractionDigits: decimais });

  useEffect(() => {
    if (open) {
      setTimeout(() => buscaRef.current?.focus(), 100);
      return;
    }

    // Ao fechar (X, ESC ou clique fora), zera tudo — produto selecionado, quantidade, preço,
    // busca e página — pra reabrir sempre do zero, em vez de continuar com o que estava
    // preenchido da última vez.
    setProdutoSelecionado(null);
    setQtdDigits('1');
    setPrecoUnitario(0);
    setSelectedIndex(0);
    setFiltro('');
    setTipoBusca('descricao');
    setModoBusca('inicio');
    setPage(0);
  }, [open]);

  useEffect(() => {
    if (tabelaRef.current && selectedIndex >= 0) {
      const selectedRow = tabelaRef.current.querySelector(`[data-row-index="${selectedIndex}"]`);
      if (selectedRow) {
        selectedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedIndex]);
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F8') {
        event.preventDefault();
        const tipos = ['descricao', 'referencia', 'codinterno'];
        const currentIndex = tipos.indexOf(tipoBusca);
        const nextIndex = (currentIndex + 1) % tipos.length;
        setTipoBusca(tipos[nextIndex]);
        setFiltro('');
        setSelectedIndex(0);
        setPage(0);
        setTimeout(() => buscaRef.current?.focus(), 100);
      }

      // Atalho documentado no rodapé desta dialog — foca direto o campo de quantidade, útil
      // depois de já ter um produto selecionado sem precisar clicar no campo.
      if (event.key === 'F2') {
        event.preventDefault();
        qtdInputRef.current?.focus();
        qtdInputRef.current?.select();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleGlobalKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [open, tipoBusca]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Dialog de confirmação de estoque aberto (renderizado via portal, mas os eventos de
    // teclado ainda bubbleiam pela árvore React até este handler) — deixa a própria dialog
    // MUI tratar Enter/Escape, sem também navegar/selecionar por baixo dela.
    if (produtoSemEstoque) return;

    if (produtos.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (selectedIndex < produtos.length - 1) {
          setSelectedIndex(prev => prev + 1);
        } else if (page + 1 < Math.ceil(totalCount / rowsPerPage)) {
          // Último item da página atual: pula pra próxima página e foca o primeiro item dela.
          focoNaTrocaDePaginaRef.current = 'primeiro';
          setPage(prev => prev + 1);
        }
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (selectedIndex > 0) {
          setSelectedIndex(prev => prev - 1);
        } else if (page > 0) {
          // Primeiro item da página atual: volta pra página anterior e foca o último item dela.
          focoNaTrocaDePaginaRef.current = 'ultimo';
          setPage(prev => prev - 1);
        }
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const prod = produtos[selectedIndex];
        if (prod) {
          selecionarProduto(prod, selectedIndex);
        }
      }
    }
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const selecionarProduto = (prod: IListagemDeProdutos, index: number) => {

    const estoqueDisponivel = prod.quan ?? 0;
    if (est && !permitirSemEstoque && estoqueDisponivel <= 0) {
      setProdutoSemEstoque(prod);
      return;
    }
    setSelectedIndex(index);
    setProdutoSelecionado(prod);
    setQtdDigits('1');
    setPrecoUnitario(prod.pvenda);

    setSelectedIndex(-1);

    buscaRef.current?.blur();

    setTimeout(() => {
      if (qtdInputRef.current) {
        qtdInputRef.current.focus();
        qtdInputRef.current.select();
      }
    }, 100);
  };

  useEffect(() => {
    if (produtoSelecionado) {
      setQtdDigits('1');
      setPrecoUnitario(produtoSelecionado.pvenda);
    }
  }, [produtoSelecionado]);

  const adicionarItem = () => {
    if (!produtoSelecionado) {
      setMsgType('warning');
      setMsg('Selecione um produto primeiro!');
      setOpenMsg(true);
      return;
    }

    // ✅ VALIDAÇÃO: Quantidade deve ser maior que 0
    if (qtd <= 0) {
      setMsgType('warning');
      setMsg('A quantidade deve ser maior que zero!');
      setOpenMsg(true);

      setTimeout(() => {
        qtdInputRef.current?.focus();
        qtdInputRef.current?.select();
      }, 100);

      return;
    }

    // ✅ VALIDAÇÃO: Unidade não pode ser vazia
    if (!produtoSelecionado.un || produtoSelecionado.un.trim() === '') {
      setMsgType('error');
      setMsg('Produto sem unidade cadastrada! Configure a unidade do produto antes de adicionar.');
      setOpenMsg(true);
      return;
    }

    // ✅ VALIDAÇÃO: Preço unitário deve ser maior que 0
    if (precoUnitario <= 0) {
      setMsgType('warning');
      setMsg('O preço unitário deve ser maior que zero!');
      setOpenMsg(true);

      setTimeout(() => {
        precoInputRef.current?.focus();
        precoInputRef.current?.select();
      }, 100);

      return;
    }

    // Desconto máximo permitido no preço unitário: até 20% abaixo do preço de venda cadastrado,
    // e nunca abaixo do preço de custo (mesma fórmula de DetalheDeProduto.tsx#calcularPrecoCusto:
    // preço de compra + comissão% + icms% + frete% + seguro fixo).
    const PERCENTUAL_MAXIMO_DESCONTO = 0.2;
    const precoMinimoDesconto = produtoSelecionado.pvenda * (1 - PERCENTUAL_MAXIMO_DESCONTO);
    const precoCusto = calcularPrecoCusto(produtoSelecionado);
    const precoMinimoPermitido = Number(
      Math.max(precoMinimoDesconto, precoCusto).toFixed(decimais)
    );

    if (precoUnitario < precoMinimoPermitido) {
      const motivo = precoCusto > precoMinimoDesconto
        ? 'o preço não pode ficar abaixo do preço de custo'
        : 'o desconto máximo permitido é de 20% sobre o preço de venda';
      setMsgType('warning');
      setMsg(
        `Preço muito baixo: ${motivo}. Preço mínimo: ${formatNumber(precoMinimoPermitido, { style: 'currency', currency: 'BRL' })}`
      );
      setOpenMsg(true);

      setTimeout(() => {
        precoInputRef.current?.focus();
        precoInputRef.current?.select();
      }, 100);

      return;
    }

    const estoqueDisponivel = produtoSelecionado.quan ?? 0;
    const preco = precoUnitario;
    const novaQtd = qtd;

    if (est && !permitirSemEstoque && novaQtd > estoqueDisponivel) {
      setMsgType('warning');
      setMsg(`Quantidade maior que o estoque disponível (${estoqueDisponivel})`);
      setOpenMsg(true);
      return;
    }

    const itemExistente = itensAtuais.find(item => item.idprodutos === produtoSelecionado.idproduto);

    let itemFinal: IitensEntradaDialog;

    if (itemExistente) {
      const quantidadeComparacao =
        itemExistente.status === 1
          ? novaQtd
          : itemExistente.qtds + novaQtd;

      const quantidadeFinal = itemExistente.qtds + novaQtd;

      if (est && !permitirSemEstoque && quantidadeComparacao > estoqueDisponivel) {
        setMsgType('warning');

        if (itemExistente.status === 1) {
          setMsg(
            `Quantidade informada (${novaQtd}) maior que o estoque disponível (${estoqueDisponivel})`
          );
        } else {
          setMsg(
            `Estoque insuficiente! Você já tem ${itemExistente.qtds} item(ns) adicionado(s) e tentou adicionar mais ${novaQtd}, porém há apenas ${estoqueDisponivel} disponível(is) em estoque.`
          );
        }
        setOpenMsg(true);
        return;
      }

      const novoTotalSomado = Number(
        (quantidadeFinal * preco).toFixed(decimais)
      );

      itemFinal = {
        ...itemExistente,
        qtds: quantidadeFinal,
        subtotal: novoTotalSomado,
        preco: preco,
        status: 2,
      };
    } else {
      const novoTotal = Number((novaQtd * preco).toFixed(decimais));

      itemFinal = {
        iditens: uuidv4(),
        idprodutos: produtoSelecionado.idproduto,
        idref: produtoSelecionado.idref,
        qtds: novaQtd,
        qtde: 0,
        subtotal: novoTotal,
        desconto: 0,
        voutros: 0,
        un: produtoSelecionado.un,
        preco: preco,
        total: 0,
        filtro: 'descricao',
        descricao: produtoSelecionado.descricao,
        ean: produtoSelecionado.ean,
        referencia: produtoSelecionado.referencia,
        marca: produtoSelecionado.marca,
        anp: produtoSelecionado.anp,
        original: produtoSelecionado.original,
        localizado: produtoSelecionado.localizado,
        status: 2,
      };
    }

    // Salva o item imediatamente na venda (rows do pai) — não fica retido nesta dialog até um
    // "confirmar" no final.
    onAdicionarItem(itemFinal);

    setMsgType('success');
    setMsg(`Item adicionado com sucesso! ${itemFinal.descricao} x ${formatNumber(itemFinal.qtds)}`);
    setOpenMsg(true);

    setProdutoSelecionado(null);
    setPrecoUnitario(0);
    setQtdDigits('1');
    setSelectedIndex(-1);
    setFiltro('');

    setTimeout(() => {
      if (buscaRef.current) {
        buscaRef.current.focus();
        buscaRef.current.select();
      }
      setLimparFiltro(false);
    }, 100);
  };

  const irParaAjusteDeEstoque = () => {
    if (produtoSemEstoque) {
      window.open(`/produto/${produtoSemEstoque.idproduto}`, '_blank', 'noopener,noreferrer');
    }
    setProdutoSemEstoque(null);
  };

  // Enquanto o usuário navega pela listagem (setas ↑ ↓, via selectedIndex) mostra as
  // observações do produto em foco; depois que ele confirma um produto (Enter/clique), o
  // selectedIndex é resetado para -1 e passamos a mostrar as observações do produtoSelecionado.
  const produtoEmFoco = selectedIndex >= 0 ? produtos[selectedIndex] : produtoSelecionado;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={telaPequena}
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: telaPequena ? 0 : 2,
          overflow: 'hidden',
          // Tela pequena: fullScreen já cuida de preencher 100% da viewport — um width/height
          // próprio aqui, mesmo que "responsivo", brigaria com as classes que o fullScreen
          // aplica. Tela normal: mesmos valores de sempre (95%/90vh, sem teto fixo em px pra
          // não travar calcularLimiteDinamico em telas grandes, ver comentário original).
          ...(telaPequena ? {} : {
            width: '95%',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '95vh',
            minHeight: '420px',
          }),
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={msgType === 'error' ? null : 6000}
        onClose={() => setOpenMsg(false)}
      >
        <Alert onClose={() => setOpenMsg(false)} severity={msgType} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>

      {/* Confirmação exibida ao tentar selecionar um produto sem estoque disponível — oferece
          ir direto ao cadastro do produto (em outra aba) para ajustar o estoque, sem perder o
          contexto desta venda em andamento. */}
      <Dialog
        open={!!produtoSemEstoque}
        onClose={() => setProdutoSemEstoque(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberRoundedIcon sx={{ color: PALETA.atencao }} />
          Produto sem estoque
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            O produto <strong>{produtoSemEstoque?.descricao}</strong> não possui estoque disponível.
            Deseja abrir o cadastro dele em outra aba para ajustar o estoque?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setProdutoSemEstoque(null)} sx={{ textTransform: 'none' }}>
            Não
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={irParaAjusteDeEstoque}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            Sim, ajustar estoque
          </Button>
        </DialogActions>
      </Dialog>

      {/* CABEÇALHO — fechar (X/ESC) + título à esquerda, 3 cards de estatística à direita
          (itens/total da venda de verdade, vindos do pai via itensAtuais — não de uma soma
          local desta dialog). */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 2 },
          px: { xs: 1.25, sm: 1.75, md: 2.5 },
          py: { xs: 1, sm: 1.5, md: 2 },
          borderBottom: `1px solid ${theme.torq.border.default}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ border: `1px solid ${theme.torq.border.default}`, borderRadius: '10px', width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 } }}
            >
              <CloseIcon fontSize="small" sx={{ color: theme.torq.text.primary }} />
            </IconButton>
            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 600 }}>ESC</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' }, fontWeight: 800, color: theme.torq.text.primary, lineHeight: 1.2 }}>
              Adicionar Item à Venda
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.8rem' }, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
              Selecione um produto e pressione ENTER
            </Typography>
            {/* Celular: resumo da venda numa linha só, no lugar dos cartões. */}
            {celular && (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                {formatNumber(qtdItens)} {qtdItens === 1 ? 'item' : 'itens'} na venda ·{' '}
                <Box component="span" sx={{ fontWeight: 700, color: PALETA.sucesso }}>{formatMoney(totalValor)}</Box>
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: celular ? 'none' : 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1.25 } }}>
          <CardEstatistica
            icon={<ShoppingCartRoundedIcon sx={{ color: theme.palette.primary.dark, fontSize: { xs: 20, sm: 26 } }} />}
            label="Itens na venda"
            valor={formatNumber(qtdItens)}
          />
          <CardEstatistica
            icon={<PaidRoundedIcon sx={{ color: PALETA.sucesso, fontSize: { xs: 20, sm: 26 } }} />}
            label="Total da venda"
            valor={formatMoney(totalValor)}
            corValor={PALETA.sucesso}
          />
          <CardEstatistica
            icon={<KeyboardRoundedIcon sx={{ color: theme.torq.text.primary, fontSize: { xs: 20, sm: 26 } }} />}
            label="Teclas de atalho"
            valor="F8 • ENTER • ESC"
          />
        </Box>
      </Box>

      {/* CONTEÚDO — busca + tabela, ocupa o espaço restante (flex:1) entre cabeçalho e o
          painel do produto selecionado (que fica sempre visível, fora da área rolável). */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: { xs: 0.75, sm: 1.25 }, p: { xs: 1, sm: 1.5, md: 2 }, overflow: 'hidden' }}>
        {/* BARRA DE BUSCA - mesmo padrão da listagem de produtos (OrdenarPorControl + ListagemBuscaField) */}
        <Box
          sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: celular ? 1 : 1.5, flexShrink: 0,
            // Celular: campo de busca em cima, largura toda; os dois seletores lado a lado embaixo.
            ...(celular && {
              '& > *:nth-of-type(1), & > *:nth-of-type(3)': { flex: '1 1 0', minWidth: 0 },
              '& > *:nth-of-type(2)': { order: -1, flex: '1 1 100%' },
            }),
          }}
        >
          <OrdenarPorControl
            opcoes={tiposBuscaProduto}
            valorSelecionado={tipoBusca}
            label={celular ? 'Buscar por' : 'Buscar por (F8)'}
            minWidth={celular ? 0 : 190}
            onChange={(novoTipo) => {
              setTipoBusca(novoTipo);
              setFiltro('');
              setSelectedIndex(0);
              setPage(0);
            }}
          />

          <ListagemBuscaField
            value={filtro}
            onChange={(valor) => {
              setFiltro(valor);
              setSelectedIndex(0);
              setPage(0);
            }}
            placeholder={celular ? 'Buscar produto...' : 'Digite para buscar produto... (F8 alterna o campo de busca)'}
            inputRef={buscaRef}
          />

          <OrdenarPorControl
            opcoes={modosDeBuscaProduto}
            valorSelecionado={modoBusca}
            label="Modo de busca"
            minWidth={celular ? 0 : 190}
            onChange={(novoModo) => {
              setModoBusca(novoModo);
              setSelectedIndex(0);
              setPage(0);
            }}
          />
        </Box>

        {/* TABELA DE PRODUTOS - mesmo layout/estilo da listagem de produtos (ListagemDeProdutos.tsx) */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              borderRadius: '14px',
              overflow: 'hidden',
              border: `1px solid ${theme.torq.border.default}`,
              backgroundColor: theme.torq.surface.card,
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                // Reserva o espaço da barra de rolagem vertical mesmo quando ela não é
                // necessária. Sem isso, cada vez que o número de linhas muda, a barra
                // vertical aparece/some, a largura útil muda, o que pode empurrar a
                // tabela para dentro/fora do overflow horizontal — a barra horizontal
                // então aparece/some também, alterando a altura útil e retroalimentando
                // o cálculo de linhas por página em um loop.
                scrollbarGutter: 'stable',
              }}
              ref={tabelaRef}
            >
              {/* Celular: produtos em cartões — nome, código/marca/ref, estoque e
                  preço. Tocar seleciona (mesmo selecionarProduto da tabela). */}
              {celular ? (
                <Box>
                  {produtos.map((prod, index) => {
                    const isSelected = produtoSelecionado?.idproduto === prod.idproduto;
                    const estoqueNegativo = (prod.quan ?? 0) < 0;
                    const estoqueZerado = (prod.quan ?? 0) === 0;
                    const detalhes = [prod.codinterno, prod.marca, prod.referencia ? `Ref ${prod.referencia}` : null, prod.localizado]
                      .filter(Boolean).join(' · ');
                    return (
                      <Box
                        key={prod.idproduto}
                        data-row-index={index}
                        onClick={() => selecionarProduto(prod, index)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          cursor: 'pointer',
                          borderBottom: `1px solid ${theme.torq.border.default}`,
                          borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          '&:active': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: theme.torq.text.primary, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {prod.descricao}
                          </Typography>
                          {detalhes && (
                            <Typography noWrap sx={{ fontSize: '0.72rem', color: theme.torq.text.secondary }}>
                              {detalhes}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: estoqueNegativo ? 'error.dark' : estoqueZerado ? theme.torq.text.muted : PALETA.sucesso }}>
                              Estoque: {formatNumber(prod.quan ?? 0)} {prod.un}
                            </Typography>
                            {!prod.original && (
                              <Chip label="Similar" size="small" sx={{ height: 16, fontSize: '0.62rem', fontWeight: 600, '& .MuiChip-label': { px: 0.75 }, bgcolor: alpha(PALETA.atencao, 0.16), color: PALETA.atencao }} />
                            )}
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: PALETA.sucesso, whiteSpace: 'nowrap' }}>
                          {formatMoney(prod.pvenda)}
                        </Typography>
                      </Box>
                    );
                  })}

                  {produtos.length === 0 && (
                    <Box textAlign="center" py={4} px={2}>
                      {isLoading ? (
                        <CircularProgress size={26} thickness={4} />
                      ) : (
                        <>
                          <ImageIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {filtro ? 'Nenhum produto encontrado — ajuste a busca' : 'Nenhum produto encontrado'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}

                  {isLoading && produtos.length > 0 && <LinearProgress sx={{ height: '1.5px' }} />}

                  {totalCount > rowsPerPage && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} py={1.25}>
                      <Pagination
                        page={page + 1}
                        count={Math.ceil(totalCount / rowsPerPage)}
                        onChange={(_, newPage) => setPage(newPage - 1)}
                        color="primary"
                        size="small"
                        shape="rounded"
                        siblingCount={0}
                        boundaryCount={1}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Total de {totalCount} produto{totalCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
              <Table size="small" stickyHeader sx={{ minWidth: '100%' }}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        backgroundColor: theme.torq.surface.level2,
                        color: theme.torq.text.secondary,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        borderBottom: `1px solid ${theme.torq.border.default}`,
                      },
                    }}
                  >
                    <TableCell sx={{ width: '32%' }}>Produto</TableCell>
                    <TableCell sx={{ width: '8%' }}>Cód.</TableCell>
                    <TableCell sx={{ width: '6%' }}>Un</TableCell>
                    <TableCell sx={{ width: '9%' }}>Qtd. Disp.</TableCell>
                    <TableCell sx={{ width: '11%' }}>Marca</TableCell>
                    <TableCell sx={{ width: '11%' }}>Referência</TableCell>
                    <TableCell sx={{ width: '8%' }}>Tipo</TableCell>
                    <TableCell sx={{ width: '8%' }}>Local</TableCell>
                    <TableCell align='right' sx={{ width: '10%' }}>Valor</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {produtos.map((prod, index) => {
                    const isFocused = index === selectedIndex;
                    const isSelected = produtoSelecionado?.idproduto === prod.idproduto;
                    const estoqueNegativo = (prod.quan ?? 0) < 0;
                    const estoqueZerado = (prod.quan ?? 0) === 0;

                    return (
                      <TableRow
                        key={prod.idproduto}
                        data-row-index={index}
                        onClick={() => selecionarProduto(prod, index)}
                        hover
                        tabIndex={0}
                        sx={{
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                          '&:last-child td': { borderBottom: 0 },
                          '& td': { py: 0.75, px: 1, borderBottom: `1px solid ${theme.torq.border.default}` },
                          '&:hover': {
                            backgroundColor: isSelected
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.primary.main, 0.035),
                          },
                          // Indicador de qual linha está com o foco do teclado (setas ↑ ↓).
                          ...(isFocused && {
                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: '-2px',
                          }),
                        }}
                      >
                        <TableCell>
                          <Box display='flex' alignItems='center' gap={1.25}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.dark,
                                flexShrink: 0,
                              }}
                            >
                              <Inventory2RoundedIcon sx={{ fontSize: '15px' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                noWrap
                                sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.torq.text.primary }}
                              >
                                {prod.descricao}
                              </Typography>
                              {prod.infadd && (
                                <Typography noWrap sx={{ fontSize: '0.7rem', color: theme.torq.text.secondary }}>
                                  {prod.infadd}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.codinterno || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.un}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              color: estoqueNegativo ? 'error.dark' : estoqueZerado ? theme.torq.text.muted : PALETA.sucesso,
                            }}
                          >
                            {formatNumber(prod.quan ?? 0)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.marca || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.referencia || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={prod.original ? 'Própria' : 'Similar'}
                            size="small"
                            sx={{
                              height: '18px',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                              '& .MuiChip-label': { px: 1 },
                              bgcolor: prod.original ? alpha(PALETA.sucesso, 0.12) : alpha(PALETA.atencao, 0.16),
                              color: prod.original ? PALETA.sucesso : PALETA.atencao,
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                            {prod.localizado || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align='right'>
                          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, color: PALETA.sucesso }}>
                            {formatMoney(prod.pvenda)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>

                {produtos.length === 0 && isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                          <CircularProgress size={26} thickness={4} />
                          <Typography variant='body2' color='text.secondary'>
                            Carregando produtos...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                {produtos.length === 0 && !isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <Box>
                          <ImageIcon sx={{
                            fontSize: 32,
                            color: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.text.secondary, 0.4)
                              : 'text.secondary',
                            mb: 1
                          }} />
                          <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                            {Environment.LISTAGEM_VAZIA}
                          </Typography>
                          <Typography variant='body2' color='text.secondary' fontSize="0.75rem">
                            {filtro ? 'Tente ajustar os filtros de busca' : 'Nenhum produto encontrado'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}

                <TableFooter>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ p: 0 }}>
                        <LinearProgress sx={{ height: '1.5px' }} />
                      </TableCell>
                    </TableRow>
                  )}
                  {totalCount > rowsPerPage && (
                    <TableRow ref={footerRowRef}>
                      <TableCell colSpan={9} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                          <Pagination
                            page={page + 1}
                            count={Math.ceil(totalCount / rowsPerPage)}
                            onChange={(_, newPage) => setPage(newPage - 1)}
                            color="primary"
                            size="small"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                            siblingCount={1}
                            boundaryCount={1}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Total de {totalCount} produto{totalCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              </Table>
              )}
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* PAINEL DO PRODUTO SELECIONADO — descrição + detalhes à esquerda, quantidade/preço/
          total + botão Adicionar à direita. Fora da área rolável (flexShrink:0), sempre
          visível. */}
      <Box sx={{ flexShrink: 0, px: { xs: 1, sm: 1.5, md: 2 }, pb: celular ? 'calc(8px + env(safe-area-inset-bottom))' : { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            p: { xs: 1.25, sm: 1.5, md: 2 },
            borderRadius: '12px',
            border: `1px solid ${theme.torq.border.default}`,
            backgroundColor: theme.torq.surface.level2,
          }}
        >
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, minWidth: 0, flex: celular ? '1 1 100%' : '1 1 280px' }}>
            <ArticleRoundedIcon sx={{ color: theme.palette.primary.dark, fontSize: { xs: 22, sm: 28 }, mt: 0.25, flexShrink: 0, display: celular ? 'none' : 'block' }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: theme.palette.primary.dark, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Produto selecionado
              </Typography>
              <Typography noWrap sx={{ fontWeight: 700, color: theme.torq.text.primary }}>
                {produtoSelecionado?.descricao || 'Nenhum produto selecionado'}
              </Typography>
              {produtoEmFoco?.infadd && (
                <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {produtoEmFoco.infadd}
                </Typography>
              )}
              {produtoSelecionado && !celular && (
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 700, color: theme.palette.primary.dark, textTransform: 'uppercase', letterSpacing: '0.03em', fontSize: '0.68rem' }}>
                    Detalhes:{' '}
                  </Box>
                  Marca: {produtoSelecionado.marca || '—'} • Referência: {produtoSelecionado.referencia || '—'} • Local: {produtoSelecionado.localizado || '—'} • Unidade: {produtoSelecionado.un} • Estoque disponível: {formatNumber(produtoSelecionado.quan ?? 0)}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap',
              // Celular: Quantidade e Preço lado a lado (metade cada), o total
              // vai no próprio botão Adicionar, que ocupa a linha inteira.
              ...(celular && {
                width: '100%',
                gap: 1,
                '& > .MuiTextField-root': { flex: '1 1 0', width: 'auto !important', minWidth: 0 },
              }),
            }}
          >
            <TextField
              size="small"
              label={celular ? 'Quantidade' : 'Quantidade (F2)'}
              inputRef={qtdInputRef}
              value={qtdDisplay}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                setQtdDigits(digitsOnly);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && produtoSelecionado) {
                  e.preventDefault();
                  precoInputRef.current?.focus();
                  precoInputRef.current?.select();
                }
              }}
              inputProps={{ style: { textAlign: 'center' } }}
              sx={{ width: { xs: 90, sm: 110 }, backgroundColor: theme.torq.surface.card }}
            />

            <TextField
              size="small"
              inputRef={precoInputRef}
              label="Preço unitário"
              value={formatNumber(precoUnitario, {
                minimumFractionDigits: decimais,
                maximumFractionDigits: decimais
              })}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '');
                const divisor = decimais === 4 ? 10000 : 100;
                const numberValue = Number(digitsOnly) / divisor;
                setPrecoUnitario(numberValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarItem();
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{ width: { xs: 110, sm: 140 }, backgroundColor: theme.torq.surface.card }}
            />

            {!celular && <TextField
              size="small"
              label="Total do item"
              value={formatMoney(qtd * precoUnitario)}
              disabled
              sx={{
                width: { xs: 110, sm: 140 },
                '& .MuiOutlinedInput-root.Mui-disabled': {
                  backgroundColor: alpha(PALETA.sucesso, 0.08),
                  '& fieldset': { borderColor: alpha(PALETA.sucesso, 0.4) },
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: PALETA.sucesso,
                  fontWeight: 700,
                },
              }}
            />}

            <Button
              ref={btnAddRef}
              variant="contained"
              color="primary"
              onClick={adicionarItem}
              disabled={!produtoSelecionado || qtd <= 0}
              startIcon={<ShoppingCartRoundedIcon />}
              sx={{
                height: { xs: 36, sm: 40 },
                px: { xs: 1.5, sm: 2.5 },
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                ...(celular && { width: '100%', height: 48, fontSize: '0.95rem', borderRadius: '12px' }),
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarItem();
                }
                // Botões nativos ativam no espaço por padrão: sem isso, o foco que fica no
                // botão depois de um clique deixa qualquer espaço digitado (ex.: sem querer,
                // enquanto navega pela tela) reacionar "Adicionar" e devolver o foco pra busca.
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
            >
              {celular ? `Adicionar · ${formatMoney(qtd * precoUnitario)}` : 'Adicionar (ENTER)'}
            </Button>
          </Box>
        </Box>
      </Box>

    </Dialog>
  );
};
